/**
 * AsOfNav — the "Zeitreise" (time-travel) stepper on the farm object view
 * (#1913 C). Steps the ``?asof=`` instant across the entity's recorded change
 * dates so the object view can show its state as it was then. Purely a picker
 * over the derived stops — the actual reconstruction is client-side folding of
 * the already-fetched transition series (no per-step network).
 *
 * Stops arrive newest-first. "Älter" moves to the closest older stop, "Neuer"
 * to the closest newer stop (or back to live), "Jetzt" clears ``?asof=`` and
 * returns to the current state.
 */
import React from 'react';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import type { AsOfStop } from './asof';
import { normalizeAsOf } from './asof';
import { formatDateString } from '../formatValue';
import './AsOfNav.css';

export interface AsOfNavProps {
  /** Change-date stops, newest-first (from ``deriveAsOfStops``). */
  stops: AsOfStop[];
  /** The active as-of instant, or ``null`` for the live/current view. */
  asOf: string | null;
  /** Set (instant) or clear (``null``) the as-of instant. */
  onChange: (iso: string | null) => void;
}

export const AsOfNav: React.FC<AsOfNavProps> = ({ stops, asOf, onChange }) => {
  if (stops.length === 0) return null;

  // Shared "is historical" predicate (#6): empty/unparseable asOf == live.
  const active = normalizeAsOf(asOf);
  const isLive = active === null;
  const currentMs = isLive ? Number.POSITIVE_INFINITY : Date.parse(active);
  const pristineAt = stops[stops.length - 1].at;

  // Stops are newest-first: the first stop older than the current instant is
  // the closest older one; the closest newer one is the last stop above it.
  const olderStop = stops.find((s) => Date.parse(s.at) < currentMs) ?? null;
  const newerStops = stops.filter((s) => Date.parse(s.at) > currentMs);
  const newerStop = newerStops.length > 0 ? newerStops[newerStops.length - 1] : null;

  const label = isLive
    ? 'Aktueller Stand'
    : active === pristineAt
      ? 'Ursprünglicher Stand'
      : `Stand: ${formatDateString(active)}`;

  return (
    <div className={`asof-nav${isLive ? '' : ' asof-nav--historical'}`} role="group" aria-label="Zeitreise">
      <span className="asof-nav__caption">Zeitreise</span>
      <div className="asof-nav__controls">
        <button
          type="button"
          className="asof-nav__step"
          onClick={() => olderStop && onChange(olderStop.at)}
          disabled={!olderStop}
          aria-label="Älter"
          title="Älterer Stand"
        >
          <ChevronLeft size={14} />
          Älter
        </button>
        <span className="asof-nav__label" aria-live="polite">
          {label}
        </span>
        <button
          type="button"
          className="asof-nav__step"
          onClick={() => onChange(newerStop ? newerStop.at : null)}
          disabled={isLive}
          aria-label="Neuer"
          title="Neuerer Stand"
        >
          Neuer
          <ChevronRight size={14} />
        </button>
      </div>
      <button
        type="button"
        className="asof-nav__now"
        onClick={() => onChange(null)}
        disabled={isLive}
      >
        <RotateCcw size={13} />
        Jetzt
      </button>
    </div>
  );
};

export default AsOfNav;
